import { useId, useRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { asArray, resolveConditionalDriver, resolveFieldLabel, CHIP_OPTION_LIMIT, NATIVE_SELECT_CLASS, type FieldSpec, type FileAnswer } from '@/lib/intakeFields';
// #1011 / #1098 (B-431): the preserved out-of-list option below is FAMILY-facing.
// A stored value must reach a family as the words they chose, never as its id.
import { optionLabelFor } from '@/lib/formDefinitions';
import { numericInputProps, useNumericDraft } from '@/components/editorial';
import { ShownInEnglish } from '@/components/apply/FormLanguageNotice';
import { PendingDecision } from '@/components/editorial/PendingDecision';
import type { PendingDecisionId } from '@/lib/pending-decisions';
import { UPLOAD_ACCEPT } from '@/lib/uploadTypes';
import { MemorableDateInput } from './MemorableDateInput';
import { HospitalSuggestInput } from './HospitalSuggestInput';
import { serverMessage } from '@/lib/serverMessage';
import { isUnsupportedFileTypeError } from '@/lib/uploadTypes';

/** #2441: TRF serves children aged 0–18, so the child's date of birth falls in
 *  this year or the eighteen before it (2008–2026 in 2026). */
function childBirthYears() {
  const max = new Date().getFullYear();
  return { min: max - 18, max };
}

interface Props {
  field: FieldSpec;
  value: unknown;
  onChange: (value: unknown) => void;
  onBlur?: () => void;
  // The other field values in the same section — required for a field whose
  // options depend on a sibling (F-3: the region dropdown reads `country`).
  siblingValues?: Record<string, unknown>;
  /**
   * #602 — every section's values, keyed by section id.
   *
   * Optional and default-safe: a surface that does not pass it simply resolves
   * no cross-section driver, and the control falls back to `field.options`
   * exactly as it behaved before. It exists because a driver need not be a
   * sibling — `patient.ethnicity` swaps its taxonomy on `intake.country`, and
   * `siblingValues` is section-local on every surface by construction.
   */
  sectionValues?: Record<string, Record<string, unknown> | undefined>;
  /**
   * Protected — the control renders but refuses input (AP-21 / D-SE-1).
   *
   * A protection is only legible if it says WHY, so `readOnlyReason` is
   * required alongside it rather than optional: a disabled control with no
   * explanation reads as a bug, and staff then go looking for the edit path
   * that does not exist. The reason is rendered, not just announced to
   * assistive tech.
   */
  readOnly?: boolean;
  readOnlyReason?: string;
  /**
   * D-FB-12: uploads the chosen file and resolves to the stored reference.
   *
   * Injected rather than called from here because only the surface knows WHERE
   * the bytes go — the wizard posts to its own application, and the reference
   * is minted server-side so a client can never name someone else's document.
   * Absent on the staff surfaces (`EditableFieldRow`), which have no upload
   * route to call: those render the answer without a picker rather than
   * offering a button that cannot work.
   */
  onUploadFile?: (file: File) => Promise<FileAnswer>;
  /**
   * #2109 R2-B18: is this field's string at `slot` shown in English because
   * its translation was made from older wording? `slot` is the field-relative
   * translation-stage key (`label`, `helper`, `conditionalLabel.US`,
   * `options.<value>.label`, `conditionalOptions.US.<value>.label`). The
   * marker is the one per-string note the form carries; every other language
   * note is a single form-level line. Absent: nothing is marked.
   * A placeholder carries no marker — it vanishes as the family types.
   */
  shownInEnglish?: (slot: string) => boolean;
  /** #2448: a date field's starting year when it is empty — the birth year the
   *  pre-screen's age implies. Month and day stay the family's to pick. */
  suggestedYear?: number | null;
  /**
   * #2376: the surface refused this answer and shows its own marker for it.
   * The control then carries aria-invalid and, with `errorId`, points
   * aria-describedby at that marker, so assistive tech hears the reason on
   * the control the family focuses. Both are dropped once `invalid` is false.
   */
  invalid?: boolean;
  /** #2376: the id of the surface's inline marker for this field. */
  errorId?: string;
  /**
   * The `(?)` pending decision this question's wording or answers carry, set
   * by the surface that knows it (wave 2: the grant step's labels, the
   * Canadian answer lists). Rendered beside the question, outside the
   * `<label>`, so the control's accessible name stays the question.
   */
  decision?: PendingDecisionId | readonly PendingDecisionId[];
}

/**
 * The keyboard a phone shows for a `text` question, from its named `format`
 * (`lib/intakeFields.ts` FieldSpec.format). Families fill the application on
 * phones, and a letters keyboard for a phone number or ZIP is a slower,
 * error-prone answer. `autoComplete` lets the browser offer what it already
 * knows. A ZIP gets the number pad only when the family said they live in the
 * US: a Canadian postal code carries letters ("K1A 0B1"), which a numeric pad
 * cannot type, so any other or unanswered region keeps the full keyboard.
 */
type TextKeyboardProps = Pick<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'inputMode' | 'autoComplete'
>;
function textKeyboardProps(
  format: FieldSpec['format'],
  sectionValues?: Record<string, Record<string, unknown> | undefined>,
): TextKeyboardProps {
  switch (format) {
    case 'email':
      return { type: 'email', inputMode: 'email', autoComplete: 'email' };
    case 'phone':
      return { type: 'tel', inputMode: 'tel', autoComplete: 'tel' };
    case 'zip':
      return sectionValues?.intake?.country === 'US'
        ? { type: 'text', inputMode: 'numeric', autoComplete: 'postal-code' }
        : { type: 'text', autoComplete: 'postal-code' };
    default:
      return { type: 'text' };
  }
}

/** #2376: the two attributes a refused control carries, or none. */
type ErrorAria = { 'aria-invalid'?: true; 'aria-describedby'?: string };
function errorAriaFor(invalid?: boolean, errorId?: string): ErrorAria {
  if (!invalid) return {};
  return errorId ? { 'aria-invalid': true, 'aria-describedby': errorId } : { 'aria-invalid': true };
}

/** The stage slot of the label `resolveFieldLabel` shows — the conditional
 *  label for the active driver value when one renders, else `label`. */
function fieldLabelSlot(
  field: FieldSpec,
  siblingValues?: Record<string, unknown>,
  sectionValues?: Record<string, Record<string, unknown> | undefined>,
): string {
  const conditional = field.conditionalLabel;
  if (!conditional) return 'label';
  // #2523: the same resolver `resolveFieldLabel` uses, so a cross-section
  // driver (`onSection`) picks the same slot the label came from.
  const driverKey = resolveConditionalDriver(conditional, siblingValues, sectionValues);
  return conditional.byValue[driverKey] != null ? `conditionalLabel.${driverKey}` : 'label';
}

/** The stage-slot prefix of the option list a control shows: the resolved
 *  `conditionalOptions` branch, else the static `options`. */
function optionSlotPrefix(
  field: FieldSpec,
  siblingValues?: Record<string, unknown>,
  sectionValues?: Record<string, Record<string, unknown> | undefined>,
): string {
  const conditional = field.conditionalOptions;
  if (!conditional) return 'options';
  const driverKey = resolveConditionalDriver(conditional, siblingValues, sectionValues);
  return conditional.byValue[driverKey] ? `conditionalOptions.${driverKey}` : 'options';
}

function IntakeFieldControl({ field, value, onChange, onBlur, siblingValues, sectionValues, onUploadFile, shownInEnglish, suggestedYear, invalid, errorId, decision }: Props) {
  const { t } = useTranslation('forms');
  const id = `intake-field-${field.key.replace(/\./g, '-')}`;
  // #583: the label itself can depend on a sibling (country → State vs
  // Province / territory), exactly as the option list already does. #2523: or
  // on another section's answer (the household, finances and travel questions
  // word themselves for Canada off `intake.country`).
  const label = resolveFieldLabel(field, siblingValues, sectionValues);

  // Required fields get a rose asterisk (visual cue) + the control gets
  // aria-required (assistive-tech cue). Mirrors the wizard's step-advance gate.
  const requiredMark = field.required ? (
    <span className="text-status-danger-fg ml-0.5" aria-hidden="true">*</span>
  ) : null;
  // #2109 R2-B18: one marker per stale string the family actually reads.
  const mark = (slot: string): ReactNode => (shownInEnglish?.(slot) ? <ShownInEnglish /> : null);
  const staleMark = mark(fieldLabelSlot(field, siblingValues, sectionValues));
  const optionPrefix = shownInEnglish ? optionSlotPrefix(field, siblingValues, sectionValues) : 'options';
  const optionMark = (optionValue: string) => mark(`${optionPrefix}.${optionValue}.label`);
  // A native <option> holds text only, so the note is appended as words.
  const optionSuffix = (optionValue: string) =>
    shownInEnglish?.(`${optionPrefix}.${optionValue}.label`) ? ` (${t('language.shownInEnglish')})` : '';
  const ariaRequired = field.required || undefined;
  const errorAria = errorAriaFor(invalid, errorId);

  // Walk feedback 2026-08-20 (Mario): long workbook questions were illegible
  // in the 10px mono-caps treatment — questions now read sentence-case at
  // body size, matching the pre-screen's ChoiceGroup. Mono-caps stays for
  // eyebrows/group headers only.
  const monoLabel = field.type !== 'checkbox' ? (
    <div className="mb-1.5">
      <label htmlFor={id} className={decision ? 'inline' : 'block'}>
        <span className="text-[15px] font-medium leading-snug text-foreground">{label}{requiredMark}{staleMark}</span>
      </label>
      {decision && <>{' '}<PendingDecision id={decision} side="bottom" /></>}
      {field.helper && (
        <p className="mt-0.5 text-caption text-stevens-ink-3">{field.helper}{mark('helper')}</p>
      )}
    </div>
  ) : null;

  if (field.type === 'checkbox') {
    return (
      <div className="flex items-start gap-2">
        <Checkbox
          id={id}
          checked={Boolean(value)}
          onCheckedChange={(checked) => onChange(checked === true)}
          onBlur={onBlur}
          className="mt-0.5"
          {...errorAria}
        />
        <Label htmlFor={id} className="cursor-pointer">
          <span className="text-[15px] font-medium leading-snug text-foreground">{label}{staleMark}</span>
        </Label>
      </div>
    );
  }

  if (field.type === 'textarea') {
    return (
      <div>
        {monoLabel}
        <Textarea
          id={id}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={field.placeholder}
          aria-required={ariaRequired}
          {...errorAria}
        />
      </div>
    );
  }

  if (field.type === 'text' && field.key === 'hospital') {
    // #2448: the pre-screen's partner-hospital suggestions, free text kept.
    return (
      <div>
        {monoLabel}
        <HospitalSuggestInput
          id={id}
          value={typeof value === 'string' ? value : ''}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={field.placeholder}
          ariaRequired={ariaRequired}
        />
      </div>
    );
  }

  if (field.type === 'text') {
    return (
      <div>
        {monoLabel}
        <Input
          id={id}
          {...textKeyboardProps(field.format, sectionValues)}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={field.placeholder}
          aria-required={ariaRequired}
          {...errorAria}
        />
      </div>
    );
  }

  if (field.type === 'number') {
    return (
      <div>
        {monoLabel}
        <NumberBox
          id={id}
          field={field}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          ariaRequired={ariaRequired}
          errorAria={errorAria}
        />
      </div>
    );
  }

  if (field.type === 'date') {
    return (
      <div>
        {monoLabel}
        {/* #2441: typed month / day / year — no calendar popup for a date
            the family already knows. */}
        <MemorableDateInput
          id={id}
          fieldKey={field.key}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          ariaRequired={ariaRequired}
          yearRange={field.key === 'date_of_birth' ? childBirthYears() : undefined}
          suggestedYear={suggestedYear}
          invalid={invalid}
          errorId={errorId}
        />
      </div>
    );
  }

  // The two native-<select> variants — the conditional-options select (F-3
  // region field, list driven by a sibling like `country`) and the 2027
  // workbook `display: 'dropdown'` — share one control; see its header for
  // what differs. A field carrying BOTH takes the conditional list, exactly
  // as it did when the conditional branch was the first `if` here.
  if (field.type === 'select' && (
    field.conditionalOptions
    || field.display === 'dropdown'
    || (field.options?.length ?? 0) > CHIP_OPTION_LIMIT
  )) {
    return (
      <div>
        {monoLabel}
        <SelectDropdownControl
          id={id}
          field={field}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          siblingValues={siblingValues}
          sectionValues={sectionValues}
          ariaRequired={ariaRequired}
          optionSuffix={optionSuffix}
          errorAria={errorAria}
        />
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div>
        {monoLabel}
        <div className="flex flex-wrap gap-2" role="group" aria-label={label} {...errorAria}>
          {field.options?.map((option) => {
            const active = value === option.value;
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={active}
                onClick={() => onChange(option.value)}
                onBlur={onBlur}
                className={`rounded-lg border px-3 py-2 text-body transition-colors ${
                  active
                    ? 'border-stevens-indigo bg-stevens-indigo/5 text-stevens-indigo'
                    : 'border-border hover:bg-muted text-foreground'
                }`}
              >
                {option.label}{optionMark(option.value)}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (field.type === 'multi') {
    return (
      <div>
        {monoLabel}
        <MultiControl
          field={field}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          siblingValues={siblingValues}
          sectionValues={sectionValues}
          label={label}
          optionMark={optionMark}
          errorAria={errorAria}
        />
      </div>
    );
  }

  if (field.type === 'file') {
    return (
      <div>
        {monoLabel}
        <FileControl
          id={id}
          value={value as FileAnswer | null | undefined}
          onChange={onChange}
          onUploadFile={onUploadFile}
          ariaRequired={ariaRequired}
          errorAria={errorAria}
        />
      </div>
    );
  }

  return null;
}

/**
 * D-FB-12's upload control: one file per field, named once it lands.
 *
 * Deliberately NOT a second copy of `DocumentUploadStep`'s tile — that one is
 * bound to the four code-pinned `DOC_SLOTS` and their per-kind caps, which a
 * builder-minted field has none of.
 */
function FileControl({ id, value, onChange, onUploadFile, ariaRequired, errorAria }: {
  id: string;
  value: FileAnswer | null | undefined;
  onChange: (value: unknown) => void;
  onUploadFile?: (file: File) => Promise<FileAnswer>;
  ariaRequired?: true;
  errorAria?: ErrorAria;
}) {
  const { t } = useTranslation('forms');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const uploaded = value?.source_document_id ? value : null;

  const handle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Clear the input so choosing the SAME file again still fires a change —
    // otherwise a family who hit an error cannot retry without picking
    // something else first.
    event.target.value = '';
    if (!file || !onUploadFile) return;
    setBusy(true);
    setError(null);
    try {
      onChange(await onUploadFile(file));
    } catch (err) {
      // The server's own sentence where there is one: it is the only thing
      // that knows whether this was too large, the wrong type, or refused for
      // want of consent.
      // The wrong-type refusal has a translated sentence of its own (#3115).
      setError(isUnsupportedFileTypeError(err) ? t('documents.uploadErrorType', { ns: 'forms' }) : serverMessage(err, t('input.file.failed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1.5">
      {uploaded && (
        <p className="text-[15px] text-foreground">{uploaded.filename || t('input.file.uploaded')}</p>
      )}
      {onUploadFile ? (
        <>
          <label htmlFor={id} className="inline-block cursor-pointer rounded-md border border-border px-3 py-1.5 text-caption text-stevens-ink-2 hover:bg-muted/40">
            {busy ? t('input.file.uploading') : uploaded ? t('input.file.replace') : t('input.file.choose')}
          </label>
          <input
            id={id}
            type="file"
            className="sr-only"
            accept={UPLOAD_ACCEPT}
            aria-required={ariaRequired}
            {...errorAria}
            disabled={busy}
            onChange={handle}
          />
        </>
      ) : (
        !uploaded && <p className="text-caption text-stevens-ink-3">{t('input.file.none')}</p>
      )}
      {error && (
        <p role="alert" className="text-caption text-status-danger-fg">{error}</p>
      )}
    </div>
  );
}

/**
 * The shared intake control, with one protection wrapper around it.
 *
 * `readOnly` is applied as a single `<fieldset disabled>` rather than threaded
 * through each control branch. That is deliberate: this component renders eight
 * field types today and will render more, and a per-branch `disabled` prop is
 * one forgotten branch away from a protected field that silently accepts input.
 * `fieldset[disabled]` disables every form control beneath it natively —
 * including the Radix checkbox and select triggers, which are buttons — so a
 * control type added later is protected before anyone remembers it exists.
 *
 * The reason is RENDERED, not merely announced: a disabled control with no
 * explanation reads as a bug, and staff then hunt for the edit path that is
 * deliberately absent (D-SE-1 — every field is editable, or protected with a
 * named reason).
 */
export function IntakeFieldInput(props: Props) {
  const { readOnly = false, readOnlyReason } = props;
  if (!readOnly) return <IntakeFieldControl {...props} />;
  // #2879: a protected single choice shows the ANSWER, not a greyed list of
  // every option. On /renew the disabled pill row still read "New applicant
  // (Year 1)" beside the renewal years, and a renewing family took it for a
  // choice they had. Every other type keeps the disabled control — its value
  // is already the only thing it shows. Still inside the disabled fieldset,
  // so the protection reads the same to every surface that checks for it.
  const control = props.field.type === 'select'
    ? <ReadOnlyChoice {...props} />
    : <IntakeFieldControl {...props} />;
  return (
    <div data-readonly="true" className="space-y-1">
      <fieldset disabled className="min-w-0 border-0 p-0 m-0 opacity-70">
        {control}
      </fieldset>
      {readOnlyReason ? (
        <p className="text-caption text-stevens-ink-3 italic">{readOnlyReason}</p>
      ) : null}
    </div>
  );
}

/**
 * #2879: a protected single choice, as words. The label is the field's own
 * resolved question and names the answer through `aria-labelledby` on a
 * `group`, so assistive tech hears "Application type, Renewal — Year 2". The
 * answer's words come from the list the control would have offered (the
 * resolved `conditionalOptions` branch, else `options`), then
 * `optionLabelFor` for a stored value outside it (#1011: a family reads the
 * words it chose, never an id), then the raw value. Nothing answered yet
 * reads as a dash rather than an empty row that looks like a rendering bug.
 */
function ReadOnlyChoice({ field, value, siblingValues, sectionValues, shownInEnglish }: Props) {
  const labelId = `intake-field-${field.key.replace(/\./g, '-')}-label`;
  const label = resolveFieldLabel(field, siblingValues, sectionValues);
  const conditional = field.conditionalOptions;
  const opts = conditional
    ? (conditional.byValue[resolveConditionalDriver(conditional, siblingValues, sectionValues)] ?? field.options ?? [])
    : (field.options ?? []);
  const strVal = typeof value === 'string' ? value : '';
  const chosen = opts.find((o) => o.value === strVal);
  const words = strVal === '' ? '\u2014' : (chosen?.label ?? optionLabelFor(field, strVal));
  const mark = (slot: string): ReactNode => (shownInEnglish?.(slot) ? <ShownInEnglish /> : null);
  const optionMark = chosen && shownInEnglish
    ? mark(`${optionSlotPrefix(field, siblingValues, sectionValues)}.${chosen.value}.label`)
    : null;
  return (
    <div role="group" aria-labelledby={labelId}>
      <div className="mb-1.5">
        <span id={labelId} className="block text-[15px] font-medium leading-snug text-foreground">
          {label}{mark(fieldLabelSlot(field, siblingValues, sectionValues))}
        </span>
        {field.helper && (
          <p className="mt-0.5 text-caption text-stevens-ink-3">{field.helper}{mark('helper')}</p>
        )}
      </div>
      <p className="text-body text-foreground">{words}{optionMark}</p>
    </div>
  );
}

/**
 * The number control. Split out so `useNumericDraft` is called unconditionally
 * — and because a numeric box now needs real state: `type="text"` means the
 * value the user is part-way through typing has to live somewhere, and the
 * parent holds a `number`, which cannot hold "−".
 *
 * The bounds are NOT on the element. Per the HTML spec `min`/`max` are inert on
 * anything but `type="number"`, so leaving them would have been a rule the
 * markup only appeared to carry; the caller's own gate enforces the range and
 * says the bound, which is where it was already enforced anyway.
 */
function NumberBox({
  id, field, value, onChange, onBlur, ariaRequired, errorAria,
}: {
  id: string;
  field: FieldSpec;
  value: unknown;
  onChange: (value: unknown) => void;
  onBlur?: () => void;
  ariaRequired?: boolean;
  errorAria?: ErrorAria;
}) {
  const draft = useNumericDraft(typeof value === 'number' ? value : null, onChange, { decimal: true });
  return (
    <Input
      id={id}
      {...numericInputProps({
        decimal: true,
        max: field.max ?? undefined,
        signed: field.min == null || field.min < 0,
      })}
      {...draft}
      onBlur={onBlur}
      placeholder={field.placeholder}
      aria-required={ariaRequired}
      {...errorAria}
    />
  );
}

/**
 * The pill-group multi control. Split out for the same reason `NumberBox` is —
 * a hook has to be called unconditionally, and this branch now needs one.
 *
 * #1537 (B-556): the hook is a mount-lived memory of every value that has ever
 * rendered in the "kept from an earlier answer" group. Deriving that group from
 * `selected` alone made a kept pill a one-way door: deselecting it dropped the
 * value from `selected`, which dropped it from `preserved`, which unmounted the
 * only control that could ever put it back. A family who mis-tapped had
 * permanently destroyed an answer they gave under another taxonomy, with no
 * undo and nothing on screen to say something irreversible had happened.
 *
 * The remembered set is filtered against the CURRENT resolved list rather than
 * against `selected`, which is what keeps the memory honest in both directions:
 * a value that is on offer never enters the group at all, and a driver flip
 * that puts a kept value back on offer moves its pill up into the offered row
 * and takes the group with it.
 *
 * Memory is deliberately mount-scoped. The trap being closed is the mis-click
 * with no undo inside one editing view; once the family leaves the surface with
 * the value deselected, that IS their answer, and resurrecting it on the next
 * mount would be us overriding them.
 */
function MultiControl({ field, value, onChange, onBlur, siblingValues, sectionValues, label, optionMark, errorAria }: {
  field: FieldSpec;
  value: unknown;
  onChange: (value: unknown) => void;
  onBlur?: () => void;
  siblingValues?: Record<string, unknown>;
  sectionValues?: Record<string, Record<string, unknown> | undefined>;
  label: string;
  /** #2109 R2-B18: the stale marker for an offered option, if any. */
  optionMark?: (optionValue: string) => ReactNode;
  errorAria?: ErrorAria;
}) {
  const { t } = useTranslation('forms');
  const selected = asArray(value);
  // #602 — a multi may swap its whole option list on a driver, the way the
  // conditional select does. Two differences from that branch, both deliberate:
  //  - an unresolved driver falls back to `field.options` rather than
  //    disabling. A select can say "pick Region first" in its own placeholder;
  //    a pill group has nowhere to say it, so a dead control is all a family
  //    would see.
  //  - preservation is per-VALUE, not one legacy option. A stored answer the
  //    resolved list does not offer (a US answer under a Canadian driver, or
  //    an option retired since) survives as a pill labelled through
  //    optionLabelFor, so changing Region never silently drops what the family
  //    already told us and never shows them a raw id (#1098). It renders in
  //    its own captioned group below the list, not inside it — see there.
  // A field with no conditionalOptions keeps its exact prior behaviour.
  const conditional = field.conditionalOptions;
  const resolved = conditional
    ? (conditional.byValue[resolveConditionalDriver(conditional, siblingValues, sectionValues)] ?? field.options ?? [])
    : (field.options ?? []);
  // #1537 — the union is written during render, on purpose: a value can arrive
  // out-of-list from a driver change rather than from a click, so an event
  // handler would miss it. Writing it is idempotent (a Set), so a StrictMode
  // double render records nothing twice, and insertion order gives the kept
  // pills a stable position that does not jump on deselect/re-select.
  const keptRef = useRef<Set<string>>(new Set());
  if (conditional) {
    for (const v of selected) {
      if (!resolved.some((o) => o.value === v)) keptRef.current.add(v);
    }
  }
  const preserved = conditional
    ? Array.from(keptRef.current)
      .filter((v) => !resolved.some((o) => o.value === v))
      .map((v) => ({ value: v, label: optionLabelFor(field, v) }))
    : [];
  const pill = (option: { value: string; label: string }, offered: boolean) => {
    const active = selected.includes(option.value);
    return (
      <button
        key={option.value}
        type="button"
        aria-pressed={active}
        onClick={() => {
          const next = active
            ? selected.filter((v) => v !== option.value)
            : [...selected, option.value];
          onChange(next);
        }}
        onBlur={onBlur}
        className={`rounded-lg border px-3 py-2 text-body transition-colors ${
          active
            ? 'border-stevens-indigo bg-stevens-indigo/5 text-stevens-indigo'
            : 'border-border hover:bg-muted text-foreground'
        }`}
      >
        {option.label}{offered && optionMark?.(option.value)}
      </button>
    );
  };
  return (
    <>
      {resolved.length > CHIP_OPTION_LIMIT ? (
        <MultiDropdown
          options={resolved}
          selected={selected}
          onChange={onChange}
          onBlur={onBlur}
          label={label}
          optionMark={optionMark}
          errorAria={errorAria}
        />
      ) : (
        <div className="flex flex-wrap gap-2" role="group" aria-label={label} {...errorAria}>
          {resolved.map((option) => pill(option, true))}
        </div>
      )}
      {/*
        #602 — a preserved answer is NOT a peer of the resolved list, and
        appending it to the same row said it was. The two taxonomies overlap:
        `black_or_african_american` ("Black or African American", US Census)
        and `black` ("Black", Statistics Canada) name the same population
        group, so a Canadian family holding the US value used to read one flat
        row of pills containing both — a duplicate-looking pair with no way to
        tell which was theirs, and an invitation to tick both into a single
        "Patient Ethnicity" export cell.
        Splitting the row is the fix that needs no synonym table: nothing is
        renamed, nothing is hidden, nothing the family said is dropped, and
        the words they actually chose are the ones under the caption. The
        resolved list above is what is on offer NOW; this is what is already
        on record. #1537: once an answer has been shown here it stays rendered
        for the life of the mount even after it is deselected — inactive, and
        one click from coming back — so the group is a record of what the
        family told us, not a door that locks behind a mis-tap.
      */}
      {preserved.length > 0 && (
        <div className="mt-3">
          <p className="text-caption text-stevens-ink-3">
            {/* #1537: no longer "still selected" — a deselected kept pill is
                still on screen, and each pill's aria-pressed says which. */}
            {t('input.kept.caption')}
          </p>
          <div
            className="mt-1.5 flex flex-wrap gap-2"
            role="group"
            aria-label={t('input.kept.groupLabel', { label })}
          >
            {preserved.map((option) => pill(option, false))}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * #2462: a "select all that apply" question with more than CHIP_OPTION_LIMIT
 * options — a closed control that names what is picked, opening onto a
 * checklist. Selection order is the family's click order, as with the pills.
 */
function MultiDropdown({ options, selected, onChange, onBlur, label, optionMark, errorAria }: {
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (value: unknown) => void;
  onBlur?: () => void;
  label: string;
  optionMark?: (optionValue: string) => ReactNode;
  errorAria?: ErrorAria;
}) {
  const { t } = useTranslation('forms');
  const idBase = useId();
  const picked = options.filter((o) => selected.includes(o.value)).map((o) => o.label);
  return (
    <Popover onOpenChange={(open) => { if (!open) onBlur?.(); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          {...errorAria}
          className={cn(NATIVE_SELECT_CLASS, 'h-auto min-h-10 items-center justify-between gap-2 py-2 text-start')}
        >
          <span className={picked.length ? 'text-foreground' : 'text-muted-foreground'}>
            {picked.length ? picked.join(', ') : t('select.placeholder')}
          </span>
          <ChevronDown size={16} className="shrink-0 opacity-60" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] max-h-80 overflow-y-auto p-1">
        <div role="group" aria-label={label}>
          {options.map((option) => {
            const checked = selected.includes(option.value);
            const optId = `${idBase}-${option.value}`;
            return (
              <label
                key={option.value}
                htmlFor={optId}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-2 text-body hover:bg-muted"
              >
                <Checkbox
                  id={optId}
                  checked={checked}
                  onCheckedChange={() => onChange(
                    checked ? selected.filter((v) => v !== option.value) : [...selected, option.value],
                  )}
                />
                <span>{option.label}{optionMark?.(option.value)}</span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The native-select control: both the conditional-options select (F-3 region)
 * and the workbook `display: 'dropdown'` variant. Split out for the same
 * reason `NumberBox` is — a hook has to be called unconditionally, and both
 * branches now need one. One component, not two: the branches differ only in
 * how the option list, placeholder, and disabled gate derive from
 * `conditionalOptions`, and everything else is byte-identical.
 *
 * #1570 (B-561): the hook is `MultiControl`'s #1537 memory, ported — a
 * mount-lived record of every value that has ever rendered as the out-of-list
 * passthrough option. Deriving that option from the LIVE value made it a
 * one-way door: picking a listed option dropped the value, which unmounted
 * the only <option> that could ever put the earlier answer back. The memory
 * is written during render, on purpose — a value can go out-of-list via a
 * driver flip on a rerender, never passing through onChange — and the write
 * is idempotent (a Set), so a StrictMode double render records nothing twice.
 * The kept render is filtered against the CURRENT resolved list: a value on
 * offer never renders twice, and a driver flip that re-offers it moves it
 * back into the list. Unlike the multi's conditional-only gate, the memory
 * runs for BOTH variants — a plain multi deliberately has no preservation at
 * all, but both select variants already preserve, so this is parity.
 *
 * Memory is deliberately mount-scoped (#1537's rationale): once the family
 * leaves the surface with another answer, that IS their answer, and
 * resurrecting the old one on the next mount would be us overriding them.
 *
 * The conditional variant's disabled gate reads `kept`, not the live value: a
 * kept option IS a real selectable option — the family's own earlier answer —
 * and disabling the control once the value is cleared would make a driverless
 * mis-clear irreversible, the same door on another path. Every state enabled
 * before stays enabled (a legacy live value is always in `kept`). The
 * placeholder stays on `hasOptions`: the LIVE list is still gated on the
 * driver, so "Select … first" remains true on an enabled-but-driverless
 * control.
 *
 * No captioned "kept from an earlier answer" <optgroup>, deliberately
 * breaking symmetry with the multi: the multi needed the caption because a
 * kept pill is visually identical to an offered pill in the same plane, while
 * here the control is single-value and the #602 overlap confusion was a
 * multi-taxonomy problem — and optgroup labels render as dead rows on the
 * mobile wheel pickers our families actually use. Revisit only if an
 * overlapping single-select taxonomy ships.
 */
function SelectDropdownControl({ id, field, value, onChange, onBlur, siblingValues, sectionValues, ariaRequired, optionSuffix, errorAria }: {
  id: string;
  field: FieldSpec;
  value: unknown;
  onChange: (value: unknown) => void;
  onBlur?: () => void;
  siblingValues?: Record<string, unknown>;
  sectionValues?: Record<string, Record<string, unknown> | undefined>;
  ariaRequired?: boolean;
  /** #2109 R2-B18: words appended to a stale option's text, else ''. */
  optionSuffix?: (optionValue: string) => string;
  errorAria?: ErrorAria;
}) {
  const { t } = useTranslation('forms');
  const conditional = field.conditionalOptions;
  // `resolveConditionalDriver` stays gated on `conditional` — it owns the
  // #602 dev warning, and the plain dropdown must not start warning.
  // #2399: an unresolved driver falls back to the field's own static options,
  // as `MultiControl` does and as `hasAnythingToPick` assumes on both sides of
  // the stack. A conditional select with no static options (Region → state)
  // still resolves to an empty list and keeps its "Select Region first" gate.
  const opts = conditional
    ? (conditional.byValue[resolveConditionalDriver(conditional, siblingValues, sectionValues)] ?? field.options ?? [])
    : (field.options ?? []);
  const strVal = typeof value === 'string' ? value : '';
  const hasOptions = opts.length > 0;
  // #1570 — the render-time write; see the header for why not a handler.
  const keptRef = useRef<Set<string>>(new Set());
  if (strVal !== '' && !opts.some((o) => o.value === strVal)) keptRef.current.add(strVal);
  const kept = Array.from(keptRef.current).filter((v) => !opts.some((o) => o.value === v));
  return (
    <select
      id={id}
      value={strVal}
      onChange={(e) => onChange(e.target.value || null)}
      onBlur={onBlur}
      aria-required={ariaRequired}
      {...errorAria}
      disabled={conditional ? !hasOptions && kept.length === 0 : undefined}
      className={NATIVE_SELECT_CLASS}
    >
      <option value="">
        {/* B-403: name the driver as the FORM labels it, never by its
            storage key — the key is `country`, the question reads
            "Region", and a hint naming the key strands the applicant. */}
        {conditional && !hasOptions
          ? (conditional.onLabel
            ? t('select.pickDriverFirst', { driver: conditional.onLabel })
            : t('select.pickPreviousFirst'))
          : t('select.placeholder')}
      </option>
      {kept.map((v) => (
        <option key={v} value={v}>
          {optionLabelFor(field, v)}
        </option>
      ))}
      {opts.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}{optionSuffix?.(option.value)}
        </option>
      ))}
    </select>
  );
}
